/* eslint-disable @atlassian/relay/unused-fields -- Fields are consumed by callers; this is a plain fetchQuery helper, not a hook/fragment. */
import { graphql, fetchQuery } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';
import { createAppAri } from '@atlassian/cs-ari';
import type {
	getForgeApp_appByIdQuery as AppByIdQuery,
	getForgeApp_appByIdQuery$data as AppByIdQueryData,
} from './__generated__/getForgeApp_appByIdQuery.graphql';
import { createAtlAttributionHeader } from '../../../utils';

export type AppData = NonNullable<AppByIdQueryData['app']>;

export class GetForgeAppError extends Error {}

/**
 * Fetches Forge app metadata (name, privacy policy, terms of service, remote/extension
 * manifest data) needed by the admin consent modal.
 *
 * Plain relay `fetchQuery` equivalent of `useForgeAppQuery` — takes `relayEnvironment`
 * as a parameter instead of reading from React context, so it can be called from
 * a stateless bridge method.
 */
export const getForgeApp = (
	relayEnvironment: IEnvironment,
	appId: string,
	contextIds: string[],
): Promise<AppData> => {
	const appAri = createAppAri({ appId }).toString();

	return new Promise((resolve, reject) => {
		fetchQuery<AppByIdQuery>(
			relayEnvironment,
			graphql`
				query getForgeApp_appByIdQuery($appAri: ID!, $contextIds: [ID!]!) {
					app(id: $appAri) @required(action: THROW) {
						name
						privacyPolicy
						termsOfService
						installationsByContexts(contextIds: $contextIds) {
							edges {
								node {
									appEnvironmentVersion {
										extensions {
											nodes {
												extensionData
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{ appAri, contextIds },
			{
				networkCacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader(),
						},
					},
				},
			},
		).subscribe({
			next: (data) => {
				resolve(data.app);
			},
			error: (error: Error) => {
				reject(
					new GetForgeAppError(
						`Failed to fetch app data by its ID: ${appAri}, GraphQLError: ${JSON.stringify(error)}`,
					),
				);
			},
		});
	});
};
